<script setup lang="ts">
import {computed, ref} from 'vue';
import ModalComponent from '@/components/ModalComponent.vue';
import AuthMethodTabs from '@/features/auth-ceremony/AuthMethodTabs.vue';
import VerifyMobileId from '@/features/auth-verify/methods/VerifyMobileId.vue';
import VerifySmartId from '@/features/auth-verify/methods/VerifySmartId.vue';
import VerifyWebEid from '@/features/auth-verify/methods/VerifyWebEid.vue';
import type {VerifyTranslations} from '@/features/auth-verify/types';

defineOptions({name: 'AuthVerifyModal'});

const props = defineProps<{
    translations: VerifyTranslations;
    purpose: string;
    resourceId: string;
    /** Translation key for the modal header. Consumers vary by destructive action. */
    titleKey: string;
}>();

const emit = defineEmits<{
    (e: 'verified', token: string | null): void;
    (e: 'close'): void;
}>();

type Method = 'mobile-id' | 'web-eid' | 'smart-id';

const method = ref<Method>('mobile-id');

const t = (key: string, fallback = '') => props.translations[key] ?? fallback ?? key;

const methodTabs = computed(() => [
    {value: 'mobile-id', label: t('lbl-ident-by-mobile-id')},
    {value: 'smart-id', label: t('lbl-ident-by-smart-id')},
    {value: 'web-eid', label: t('lbl-ident-by-id-card')},
]);

function handleVerified(token: string | null) {
    emit('verified', token);
}
</script>

<template>
    <ModalComponent
        :show="true"
        :show-footer="false"
        :close-label="t('btn-close')"
        @close="emit('close')"
    >
        <template #header>
            <h2>{{ t(titleKey) }}</h2>
        </template>

        <AuthMethodTabs v-model="method" :methods="methodTabs"/>

        <VerifyMobileId v-if="method === 'mobile-id'"
                        :translations="translations"
                        :purpose="purpose"
                        :resource-id="resourceId"
                        @verified="handleVerified"
                        @cancel="emit('close')"/>
        <VerifySmartId v-else-if="method === 'smart-id'"
                       :translations="translations"
                       :purpose="purpose"
                       :resource-id="resourceId"
                       @verified="handleVerified"
                       @cancel="emit('close')"/>
        <VerifyWebEid v-else
                      :translations="translations"
                      :purpose="purpose"
                      :resource-id="resourceId"
                      @verified="handleVerified"
                      @cancel="emit('close')"/>
    </ModalComponent>
</template>

